import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMicrochip } from "react-icons/fa";

import { technologies } from "../data/technologies";

/**
 * Deliberately inverted against ServicesSection: this is a dark band with
 * glass tiles and a horizontal icon-beside-title layout, where Services is
 * a light grid of white cards with the icon stacked above. Same peach
 * family, opposite value — which also gives the page some rhythm between
 * the two light sections either side of it.
 */
function TechnologySection() {
  return (
    <section className="tech">
      <style>{`
        .tech {
          /* Peach reads brighter on navy than on white — #ff9066 hits 8:1
             here, so the full-strength brand tone is safe for text. */
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;

          position: relative;
          overflow: hidden;
          padding: clamp(60px, 8vw, 104px) 0;
          background:
            radial-gradient(60rem 34rem at 84% 6%, rgba(255,107,53,.26), transparent 60%),
            radial-gradient(52rem 34rem at 8% 96%, rgba(255,144,102,.16), transparent 60%),
            linear-gradient(160deg, #131f36 0%, #0f172a 45%, #0b1120 100%);
          color: #fff;
        }
        /* Circuit-ish rule pattern — nods at "platforms" without a texture file */
        .tech__mesh {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(255,144,102,.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,144,102,.07) 1px, transparent 1px);
          background-size: 72px 72px;
          mask-image: radial-gradient(80% 60% at 50% 45%, #000 20%, transparent 100%);
          -webkit-mask-image: radial-gradient(80% 60% at 50% 45%, #000 20%, transparent 100%);
        }

        .tech__badge {
          display: inline-flex; align-items: center; gap: 9px;
          padding: 9px 22px; border-radius: 999px;
          background: rgba(255,144,102,.14);
          border: 1px solid rgba(255,144,102,.34);
          color: var(--peach-soft);
          font-weight: 700; font-size: .82rem; letter-spacing: .08em;
        }
        .tech__title {
          margin-top: 22px; font-weight: 800; color: #fff;
          font-size: clamp(1.75rem, 4vw, 3.25rem);
          line-height: 1.15; letter-spacing: -.02em;
        }
        .tech__title em {
          font-style: normal;
          /* Keep the gradient phrase intact — without this it wraps after
             "We" on narrow screens and orphans the word onto the line above. */
          white-space: nowrap;
          background: linear-gradient(105deg, var(--peach-soft), var(--peach) 55%, var(--peach-deep));
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .tech__br { display: none; }
        @media (min-width: 576px) { .tech__br { display: inline; } }

        .tech__lead {
          max-width: 750px; margin: 14px auto 0;
          color: #cbd5e1; font-size: clamp(.96rem, 1.6vw, 1.06rem);
          line-height: 1.85;
        }

        /* Glass tile, icon beside the title rather than stacked above it */
        .tech-tile {
          display: flex; flex-direction: column; height: 100%;
          padding: 24px;
          border-radius: 18px;
          text-decoration: none;
          background: rgba(255,255,255,.045);
          border: 1px solid rgba(255,255,255,.09);
          backdrop-filter: blur(8px);
          transition: transform .26s ease, background .26s ease,
                      border-color .26s ease, box-shadow .26s ease;
        }
        .tech-tile:focus-visible {
          outline: 3px solid var(--peach); outline-offset: 3px;
        }
        .tech-tile__head {
          display: flex; align-items: center; gap: 14px; margin-bottom: 14px;
        }
        .tech-tile__icon {
          width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
          display: grid; place-items: center;
          background: rgba(255,144,102,.14);
          border: 1px solid rgba(255,144,102,.3);
          color: var(--peach);
          transition: background .26s ease, color .26s ease, transform .26s ease;
        }
        .tech-tile__name {
          margin: 0; font-weight: 700; font-size: 1.15rem; color: #fff;
        }
        .tech-tile__text {
          color: #b9c4d4; font-size: .93rem; line-height: 1.75;
          margin-bottom: 18px;
        }
        .tech-tile__foot {
          margin-top: auto; padding-top: 14px;
          border-top: 1px solid rgba(255,255,255,.09);
          display: inline-flex; align-items: center; gap: 8px;
          color: var(--peach); font-weight: 700; font-size: .9rem;
        }
        .tech-tile__foot svg { transition: transform .26s ease; }

        /* A mouse hovers; a finger can't. On touch, lib/touchMotion.js adds
           .is-live as the tile scrolls through the middle of the screen. */
        .tech-tile:hover,
        .tech-tile.is-live {
          transform: translateY(-7px);
          background: rgba(255,144,102,.09);
          border-color: rgba(255,144,102,.55);
          box-shadow: 0 20px 44px rgba(0,0,0,.45);
        }
        .tech-tile:hover .tech-tile__icon,
        .tech-tile.is-live .tech-tile__icon {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #fff; transform: scale(1.06);
        }
        .tech-tile:hover .tech-tile__foot svg,
        .tech-tile.is-live .tech-tile__foot svg { transform: translateX(5px); }

        .tech__cta {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          border: none; border-radius: 14px; color: #0f172a; font-weight: 700;
          box-shadow: 0 12px 30px rgba(255,107,53,.4);
          transition: transform .22s ease, box-shadow .22s ease;
        }
        .tech__cta:hover, .tech__cta:focus-visible {
          background: linear-gradient(135deg, #ff7d4d, #ffa07d);
          transform: translateY(-3px);
          box-shadow: 0 18px 40px rgba(255,107,53,.55);
        }
        /* Full-bleed tap target on phones */
        @media (max-width: 575.98px) { .tech__cta { width: 100%; } }

        @media (prefers-reduced-motion: reduce) {
          .tech-tile, .tech-tile__icon, .tech-tile__foot svg, .tech__cta {
            transition: none;
          }
          .tech-tile:hover, .tech__cta:hover { transform: none; }
        }
      `}</style>

      <span className="tech__mesh" />

      <Container style={{ position: "relative", zIndex: 1 }}>
        <div className="text-center mb-5" data-aos="fade-up">
          <span className="tech__badge">
            <FaMicrochip />
            TECHNOLOGY EXPERTISE
          </span>

          <h2 className="tech__title">
            Enterprise Platforms
            <br className="tech__br" />{" "}
            <em>We Support</em>
          </h2>

          <p className="tech__lead">
            Delivering enterprise integration, cloud solutions, digital
            transformation, automation, and technology modernization through
            industry-leading platforms.
          </p>
        </div>

        <Row className="g-4">
          {technologies.map((tech, i) => {
            const { Icon } = tech;
            return (
              <Col
                xl={4}
                md={6}
                key={tech.slug}
                data-aos="zoom-in"
                data-aos-delay={(i % 3) * 90}
              >
                {/* The tile is a Link, so it's already a single focusable
                    control — no role/tabIndex plumbing needed here. */}
                <Link to={`/technology/${tech.slug}`} className="tech-tile" data-live>
                  <span className="tech-tile__head">
                    <span className="tech-tile__icon">
                      <Icon size={24} />
                    </span>
                    <h3 className="tech-tile__name">{tech.title}</h3>
                  </span>

                  <p className="tech-tile__text">{tech.description}</p>

                  <span className="tech-tile__foot">
                    Explore <FaArrowRight />
                  </span>
                </Link>
              </Col>
            );
          })}
        </Row>

        <div className="text-center mt-5" data-aos="fade-up">
          <Button
            as={Link}
            to="/technology"
            size="lg"
            className="tech__cta px-4 py-3 d-inline-flex align-items-center justify-content-center gap-2"
          >
            Explore Technology Expertise
            <FaArrowRight />
          </Button>
        </div>
      </Container>
    </section>
  );
}

export default TechnologySection;
